import { useState, useMemo, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Navbar } from "@/components/Navbar";
import { useAuth } from "@/context/AuthContext";
import Wardrobe3D from "@/components/Wardrobe3D";
import { CostPanel } from "@/components/CostPanel";
import { CncPlan } from "@/components/CncPlan";
import { CheckoutDialog } from "@/components/CheckoutDialog";
import { SavedDesigns } from "@/components/SavedDesigns";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import {
  BODY_MATERIALS,
  DOOR_MATERIALS,
  BODY_COLORS,
  DOOR_COLORS,
  GLASS_COLORS,
  bodyMaterialById,
  doorMaterialById,
  bodyColorById,
  doorColorById,
  enrich3dConfig,
  computeCost,
  computeCnc,
  shelfLayout,
  shelfLayoutDetailed,
  doorSegments,
  doorUnits,
  DEFAULT_PRICES,
} from "@/lib/pricing";
import { generateQuotePdf } from "@/lib/pdf";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { DoorOpen, Ruler, PaintBucket, ShoppingCart, FileDown, Layers, AlertTriangle, Trash2, Share2 } from "lucide-react";

function clampNum(v, min, max) {
  const n = Number(v);
  if (Number.isNaN(n)) return min;
  return Math.min(max, Math.max(min, n));
}

// Serbest yazılabilir ölçü alanı: yazım sırasında kenetleme yapılmaz;
// aralık içi değer anında uygulanır, aralık dışı/boş değer blur'da otomatik düzeltilir.
function DimInput({ label, value, min, max, onCommit, testid }) {
  const [text, setText] = useState(String(value));
  const [focused, setFocused] = useState(false);
  useEffect(() => {
    if (!focused) setText(String(value));
  }, [value, focused]);

  const commit = (raw) => {
    const n = Number(raw);
    const clamped =
      raw === "" || Number.isNaN(n) ? min : Math.min(max, Math.max(min, Math.round(n)));
    onCommit(clamped);
    setText(String(clamped));
  };

  return (
    <div>
      <Label className="text-xs">{label} (cm)</Label>
      <Input
        data-testid={testid}
        type="number"
        value={focused ? text : String(value)}
        min={min}
        max={max}
        onFocus={() => {
          setFocused(true);
          setText(String(value));
        }}
        onChange={(e) => {
          const raw = e.target.value;
          setText(raw);
          const n = Number(raw);
          if (raw !== "" && !Number.isNaN(n) && n >= min && n <= max) onCommit(Math.round(n));
        }}
        onBlur={(e) => {
          setFocused(false);
          commit(e.target.value);
        }}
        onKeyDown={(e) => {
          if (e.key === "Enter") e.currentTarget.blur();
        }}
      />
    </div>
  );
}

function MaterialPicker({ label, value, onChange, options, priceMap, testid }) {
  return (
    <div>
      <Label className="flex items-center gap-1.5 mb-2">
        <Layers className="w-3.5 h-3.5 text-primary" /> {label}
      </Label>
      <div className="grid grid-cols-2 gap-2" data-testid={testid}>
        {options.map((m) => (
          <button
            key={m.id}
            type="button"
            onClick={() => onChange(m.id)}
            data-testid={`${testid}-${m.id}`}
            className={`rounded-lg border px-3 py-2 text-left transition-all ${
              value === m.id
                ? "border-primary bg-primary/10 shadow-sm"
                : "border-[#E6DFD5] hover:border-stone-300"
            }`}
          >
            <span className="block text-sm font-semibold text-stone-900">{m.label}</span>
            <span className="block text-xs font-mono text-stone-500">
              {((priceMap && priceMap[m.id]) ?? m.price).toLocaleString("tr-TR")} ₺/m²
            </span>
          </button>
        ))}
      </div>
    </div>
  );
}

function ColorPicker({ label, value, onChange, options, testid, tag = "Kastamonu Entegre" }) {
  return (
    <div>
      <Label className="flex items-center gap-1.5 mb-2">
        <PaintBucket className="w-3.5 h-3.5 text-primary" /> {label}
        {tag && <span className="ml-auto text-[10px] font-mono text-stone-400">{tag}</span>}
      </Label>
      <div className="grid grid-cols-3 gap-2" data-testid={testid}>
        {options.map((w) => (
          <button
            key={w.id}
            type="button"
            onClick={() => onChange(w.id)}
            data-testid={`${testid}-${w.id}`}
            title={`${w.code} ${w.label}`}
            className={`h-12 rounded-lg border-2 transition-all flex flex-col items-center justify-center overflow-hidden ${
              value === w.id ? "border-primary scale-[1.03] shadow-md" : "border-transparent hover:border-stone-300"
            }`}
            style={w.tex ? { backgroundImage: `url(${w.tex})`, backgroundSize: "cover", backgroundPosition: "center" } : { background: w.hex }}
          >
            <span
              className="text-[10px] font-mono font-semibold leading-tight px-1 rounded"
              style={{
                color: w.dark || w.tex ? "#F5F5F4" : "#44403C",
                background: w.tex ? "rgba(0,0,0,0.35)" : "transparent",
              }}
            >
              {w.code}
            </span>
            <span
              className="text-[10px] leading-tight px-1 rounded"
              style={{
                color: w.dark || w.tex ? "#EDEBE7" : "#57534E",
                background: w.tex ? "rgba(0,0,0,0.35)" : "transparent",
              }}
            >
              {w.label}
            </span>
          </button>
        ))}
      </div>
    </div>
  );
}

export default function Designer() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [cfg, setCfg] = useState({
    width: 120,
    height: 220,
    depth: 60,
    shelves: 3,
    doors: 2,
    dividers: 1,
    drawers: 0,
    drawerHeight: 20,
    base: "none",
    doorless: false,
    doorStyle: "swing",
    doorLayout: "full",
    bodyMaterial: "suntalam",
    doorMaterial: "suntalam",
    bodyColor: "d140",
    doorColor: "d140",
    removedDoors: [],
    removedShelves: [],
    noBackPanel: false,
    flapHandle: "handle",
  });
  const [openSignal, setOpenSignal] = useState(false);
  const [selected, setSelected] = useState(null); // { type:"door", key, label } | { type:"shelf", index, label }
  const [checkoutOpen, setCheckoutOpen] = useState(false);
  const [designs, setDesigns] = useState([]);
  const [prices, setPrices] = useState(DEFAULT_PRICES);

  const loadDesigns = () => api.get("/designs").then(({ data }) => setDesigns(data)).catch(() => {});
  useEffect(() => {
    loadDesigns();
    api.get("/prices").then(({ data }) => setPrices(data)).catch(() => {});
  }, []);

  // Paylaşım bağlantısı (?d=...) veya üyelik dönüşü tasarımı (pendingDesign) geri yükleme
  useEffect(() => {
    const d = searchParams.get("d");
    if (d) {
      try {
        const b64 = d.replace(/-/g, "+").replace(/_/g, "/");
        const padded = b64 + "=".repeat((4 - (b64.length % 4)) % 4);
        const c = JSON.parse(decodeURIComponent(escape(atob(padded))));
        setCfg((p) => ({ ...p, ...c }));
        toast.success("Paylaşılan tasarım yüklendi — dilediğiniz gibi düzenleyebilirsiniz.");
      } catch {
        toast.error("Paylaşım bağlantısı geçersiz.");
      }
      setSearchParams({}, { replace: true });
      return;
    }
    try {
      const raw = localStorage.getItem("pendingDesign");
      if (raw) {
        const c = JSON.parse(raw);
        setCfg((p) => ({ ...p, ...c }));
        localStorage.removeItem("pendingDesign");
        toast.success("Tasarımınız geri yüklendi — kaldığınız yerden devam edebilirsiniz.");
      }
    } catch {
      /* bozuk kayıt yok sayılır */
    }
  }, []);

  // Tasarım bağlantısını panoya kopyala (üyelik gerekmez)
  const shareDesign = async () => {
    const encoded = btoa(unescape(encodeURIComponent(JSON.stringify(cfg))))
      .replace(/\+/g, "-")
      .replace(/\//g, "_")
      .replace(/=+$/, "");
    const url = `${window.location.origin}/tasarim?d=${encoded}`;
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Tasarım bağlantısı kopyalandı — istediğiniz kişiyle paylaşabilirsiniz.");
    } catch {
      window.prompt("Bağlantıyı kopyalayın:", url);
    }
  };

  // Misafirse tasarımı saklayıp giriş/kayıt sayfasına yönlendirir
  const requireAuth = (reason) => {
    localStorage.setItem("pendingDesign", JSON.stringify(cfg));
    toast.info(
      reason === "pay"
        ? "Ödeme ve adres bilgileri için giriş yapın veya üye olun — tasarımınız korunur."
        : "Tasarımı kaydetmek için giriş yapın veya üye olun — tasarımınız korunur."
    );
    navigate("/login");
  };

  const full = enrich3dConfig(cfg);

  const cost = useMemo(() => computeCost(cfg, prices), [cfg, prices]);
  const cnc = useMemo(() => computeCnc(cfg), [cfg]);

  // 100 cm üzeri kapak kullanılmaz: kapak genişliği 100 cm'yi geçemez
  const minDoors = Math.max(1, Math.ceil(cfg.width / 100));
  useEffect(() => {
    if (cfg.doors < minDoors) setCfg((p) => ({ ...p, doors: minDoors }));
  }, [minDoors]);

  // Kapak sayısı ölçüye göre: kapak başına min ~30 cm, en fazla 6 kapak
  const maxDoors = Math.max(minDoors, Math.min(6, Math.floor(cfg.width / 30)));
  useEffect(() => {
    if (cfg.doors > maxDoors) setCfg((p) => ({ ...p, doors: maxDoors }));
  }, [maxDoors]);

  // Orta dikme ölçüye göre: bölme başına min ~30 cm, en fazla 5 dikme
  const maxDividers = Math.min(5, Math.max(0, Math.floor(cfg.width / 30) - 1));
  useEffect(() => {
    if (cfg.dividers > maxDividers) setCfg((p) => ({ ...p, dividers: maxDividers }));
  }, [maxDividers]);

  // Çekmeceler gövde yüksekliğine sığmalı (düşük TV ünitelerinde sınırlanır)
  const maxDrawers = Math.max(0, Math.min(4, Math.floor((cfg.height - 8) / ((cfg.drawerHeight ?? 20) + 1))));
  useEffect(() => {
    if (cfg.drawers > maxDrawers) setCfg((p) => ({ ...p, drawers: maxDrawers }));
  }, [maxDrawers]);

  // Düşer kapak yalnızca 20-60 cm yükseklikte kullanılabilir
  const flapActive = !cfg.doorless && cfg.doorStyle === "flap";
  useEffect(() => {
    if (flapActive && (cfg.height > 60 || cfg.height < 20)) {
      setCfg((p) => ({ ...p, height: clampNum(p.height, 20, 60) }));
    }
  }, [flapActive, cfg.height]);

  // Düşer kapaklı TV ünitesinde kapaklar varken çekmece kullanılamaz;
  // tüm kapaklar kaldırılırsa (veya kapaksız mod) çekmece aktif olur.
  // Ayna kapaklı (banyo üst) dolapta çekmece hiç olmaz — bölüm tamamen gizlenir.
  const remainingDoors = doorUnits(cfg).length;
  const flapDrawerBlocked = flapActive && remainingDoors > 0;
  const mirrorDrawerBlocked = cfg.doorMaterial === "ayna";
  useEffect(() => {
    if ((flapDrawerBlocked || mirrorDrawerBlocked) && cfg.drawers > 0)
      setCfg((p) => ({ ...p, drawers: 0 }));
  }, [flapDrawerBlocked, mirrorDrawerBlocked, cfg.drawers]);

  // Parçalı kapak yalnızca normal (yandan açılır) kapakta; üst+alt min 100 cm, üst+orta+alt min 140 cm
  useEffect(() => {
    const minH = cfg.doorLayout === "split3" ? 140 : 100;
    if (cfg.doorLayout !== "full" && (cfg.height < minH || cfg.doorless || cfg.doorStyle !== "swing")) {
      setCfg((p) => ({ ...p, doorLayout: "full" }));
    }
  }, [cfg.doorLayout, cfg.height, cfg.doorless, cfg.doorStyle]);

  const splitActive = !cfg.doorless && cfg.doorStyle === "swing" && cfg.doorLayout !== "full";
  const effectiveShelves = shelfLayout(cfg).length;

  // Yapısal değişiklikte geçersiz kalan kaldırma seçimlerini temizle (indeks/anahtar kayması)
  useEffect(() => {
    const H = cfg.height;
    const flap = cfg.doorStyle === "flap";
    const layout = flap ? "full" : cfg.doorLayout;
    const segCount = doorSegments(H, layout).length;
    const validDoorKeys = new Set();
    if (!cfg.doorless) {
      for (let i = 0; i < cfg.doors; i++) {
        for (let k = 0; k < segCount; k++) {
          validDoorKeys.add(segCount > 1 ? `${i}:${k}` : `${i}`);
        }
      }
    }
    const validShelfKeys = new Set();
    const shelfCols = (cfg.dividers ?? 0) + 1;
    shelfLayoutDetailed(cfg).forEach((s, i) => {
      if (s.structural) return;
      for (let j = 0; j < shelfCols; j++) validShelfKeys.add(`${i}:${j}`);
    });
    setCfg((p) => {
      const rd = (p.removedDoors || []).filter((k) => validDoorKeys.has(k));
      const rs = (p.removedShelves || []).filter((k) => validShelfKeys.has(String(k)));
      if (rd.length === (p.removedDoors || []).length && rs.length === (p.removedShelves || []).length)
        return p;
      return { ...p, removedDoors: rd, removedShelves: rs };
    });
    setSelected(null);
  }, [cfg.doors, cfg.dividers, cfg.shelves, cfg.drawers, cfg.drawerHeight, cfg.height, cfg.doorLayout, cfg.doorStyle, cfg.doorless]);

  const removeSelected = () => {
    if (!selected) return;
    if (selected.type === "door") {
      setCfg((p) => ({ ...p, removedDoors: [...new Set([...(p.removedDoors || []), selected.key])] }));
      toast.success(`${selected.label} kaldırıldı.`);
    } else {
      setCfg((p) => ({ ...p, removedShelves: [...new Set([...(p.removedShelves || []), selected.key])] }));
      toast.success(`${selected.label} kaldırıldı.`);
    }
    setSelected(null);
  };

  const restoreDoor = (key) =>
    setCfg((p) => ({ ...p, removedDoors: (p.removedDoors || []).filter((k) => k !== key) }));
  const restoreShelf = (key) =>
    setCfg((p) => ({ ...p, removedShelves: (p.removedShelves || []).filter((k) => k !== key) }));

  const shelfLabel = (key) => {
    const [i, j] = String(key).split(":").map(Number);
    return (cfg.dividers ?? 0) > 0 ? `Raf ${i + 1} • Bölme ${j + 1}` : `Raf ${i + 1}`;
  };

  const set = (k, v) => setCfg((p) => ({ ...p, [k]: v }));

  const saveDesign = async (name) => {
    if (!user) {
      requireAuth("save");
      return;
    }
    try {
      await api.post("/designs", { name, config: full, totalCost: cost.total });
      toast.success("Tasarım kaydedildi.");
      loadDesigns();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Kaydedilemedi");
    }
  };

  const loadDesign = (d) => {
    const c = d.config;
    setCfg({
      width: c.width,
      height: c.height,
      depth: c.depth,
      shelves: c.shelves,
      doors: c.doors,
      dividers: c.dividers ?? 0,
      drawers: c.drawers ?? 0,
      drawerHeight: c.drawerHeight ?? 20,
      base: c.base || "none",
      doorless: c.doorless ?? false,
      doorStyle: c.doorStyle || "swing",
      doorLayout: c.doorLayout || "full",
      bodyMaterial: c.bodyMaterial || "suntalam",
      doorMaterial: c.doorMaterial || "mdf",
      bodyColor: c.bodyColor || "d140",
      doorColor: c.doorColor || "d175",
      removedDoors: c.removedDoors || [],
      removedShelves: c.removedShelves || [],
      noBackPanel: c.noBackPanel ?? false,
      flapHandle: c.flapHandle || "handle",
    });
    toast.success(`"${d.name}" yüklendi.`);
  };

  const deleteDesign = async (id) => {
    try {
      await api.delete(`/designs/${id}`);
      loadDesigns();
      toast.success("Tasarım silindi.");
    } catch {
      toast.error("Silinemedi");
    }
  };

  const downloadPdf = async () => {
    try {
      await generateQuotePdf({ config: full, cost, cnc });
      toast.success("Teklif PDF indiriliyor.");
    } catch {
      toast.error("PDF oluşturulamadı");
    }
  };

  return (
    <div className="min-h-screen bg-atelier">
      <Navbar />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-6">
          <h1 className="font-serif text-4xl font-bold text-stone-900">Tasarım Stüdyosu</h1>
          <p className="text-stone-600">Ölçüleri girin, renkleri seçin ve dolabınızı tasarlayın.</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
          {/* 3D + controls left */}
          <div className="lg:col-span-8 space-y-6">
            <Wardrobe3D
              config={full}
              openAllSignal={openSignal}
              selected={selected}
              onSelectDoor={(key, num, suffix) =>
                setSelected({ type: "door", key, label: `Kapak ${num}${suffix}` })
              }
              onSelectShelf={(i, j, key) =>
                setSelected({ type: "shelf", key, label: shelfLabel(key) })
              }
            />
            {selected && (
              <div
                data-testid="designer-remove-bar"
                className="flex items-center justify-between gap-3 rounded-xl border border-primary/30 bg-primary/5 px-4 py-3"
              >
                <span className="text-sm text-stone-700">
                  <span className="font-semibold text-primary">{selected.label}</span> seçili
                </span>
                <div className="flex items-center gap-2">
                  <Button
                    data-testid="designer-remove-selected-btn"
                    variant="destructive"
                    size="sm"
                    className="rounded-full"
                    onClick={removeSelected}
                  >
                    <Trash2 className="w-4 h-4" /> {selected.type === "door" ? "Kapağı Kaldır" : "Rafı Kaldır"}
                  </Button>
                  <Button
                    data-testid="designer-deselect-btn"
                    variant="ghost"
                    size="sm"
                    className="rounded-full"
                    onClick={() => setSelected(null)}
                  >
                    Vazgeç
                  </Button>
                </div>
              </div>
            )}
            {((cfg.removedDoors || []).length > 0 || (cfg.removedShelves || []).length > 0) && (
              <div
                data-testid="designer-removed-list"
                className="rounded-xl border border-[#E6DFD5] bg-white/60 px-4 py-3 space-y-2"
              >
                <p className="text-xs font-semibold text-stone-500 uppercase tracking-wide">
                  Kaldırılanlar — tekrar eklemek için tıklayın
                </p>
                <div className="flex flex-wrap gap-2">
                  {(cfg.removedDoors || []).map((key) => {
                    const [i, k] = key.split(":");
                    const suffix = k !== undefined ? doorSegments(cfg.height, cfg.doorLayout)[Number(k)]?.suffix || "" : "";
                    return (
                      <button
                        key={`d-${key}`}
                        type="button"
                        data-testid={`designer-restore-door-${key.replace(":", "-")}`}
                        onClick={() => restoreDoor(key)}
                        className="rounded-full border border-[#E6DFD5] px-3 py-1 text-xs text-stone-600 hover:border-primary hover:text-primary transition-colors"
                      >
                        + Kapak {Number(i) + 1}{suffix}
                      </button>
                    );
                  })}
                  {(cfg.removedShelves || []).map((key) => (
                    <button
                      key={`s-${key}`}
                      type="button"
                      data-testid={`designer-restore-shelf-${String(key).replace(":", "-")}`}
                      onClick={() => restoreShelf(key)}
                      className="rounded-full border border-[#E6DFD5] px-3 py-1 text-xs text-stone-600 hover:border-primary hover:text-primary transition-colors"
                    >
                      + {shelfLabel(key)}
                    </button>
                  ))}
                </div>
              </div>
            )}
            <div className="flex flex-wrap gap-3">
              {!cfg.doorless && (
                <Button
                  data-testid="designer-toggle-doors-btn"
                  variant="outline"
                  className="rounded-full"
                  onClick={() => setOpenSignal((s) => !s)}
                >
                  <DoorOpen className="w-4 h-4" /> {openSignal ? "Kapakları Kapat" : "Kapakları Aç"}
                </Button>
              )}
              <Button
                data-testid="designer-share-btn"
                variant="outline"
                className="rounded-full"
                onClick={shareDesign}
              >
                <Share2 className="w-4 h-4" /> Paylaş
              </Button>
              <Button
                data-testid="designer-pdf-btn"
                variant="outline"
                className="rounded-full"
                onClick={downloadPdf}
              >
                <FileDown className="w-4 h-4" /> Teklifi PDF İndir
              </Button>
              <SavedDesigns
                designs={designs}
                onSave={saveDesign}
                onLoad={loadDesign}
                onDelete={deleteDesign}
              />
              <Button
                data-testid="designer-checkout-btn"
                className="rounded-full ml-auto"
                onClick={() => {
                  if (!user) {
                    requireAuth("pay");
                    return;
                  }
                  setCheckoutOpen(true);
                }}
              >
                <ShoppingCart className="w-4 h-4" /> Onayla ve Öde
              </Button>
            </div>

            <CncPlan cnc={cnc} />
          </div>

          {/* Controls + cost right */}
          <div className="lg:col-span-4 space-y-6 lg:sticky lg:top-24">
            <div className="card-atelier rounded-2xl p-6 space-y-5">
              <div className="flex items-center gap-2">
                <Ruler className="w-5 h-5 text-primary" />
                <h3 className="font-serif text-2xl font-bold text-stone-900">Ölçüler & Detaylar</h3>
              </div>

              <div className="grid grid-cols-3 gap-3">
                {[
                  { k: "width", label: "Genişlik", min: 40, max: 280, tid: "designer-width-input" },
                  {
                    k: "height",
                    label: "Yükseklik",
                    min: flapActive ? 20 : 30,
                    max: flapActive ? 60 : 280,
                    tid: "designer-height-input",
                  },
                  { k: "depth", label: "Derinlik", min: 15, max: 100, tid: "designer-depth-input" },
                ].map((f) => (
                  <DimInput
                    key={f.k}
                    label={f.label}
                    value={cfg[f.k]}
                    min={f.min}
                    max={f.max}
                    onCommit={(v) => set(f.k, v)}
                    testid={f.tid}
                  />
                ))}
              </div>

              <div className="flex flex-wrap gap-1.5" data-testid="designer-dim-presets">
                {[
                  {
                    label: "TV Ünitesi",
                    w: 180, h: 45, d: 45, tid: "preset-tv-unitesi",
                    extra: { doorStyle: "flap", doorless: false, doorLayout: "full", doors: 3, dividers: 2, shelves: 0, drawers: 0, base: "none", doorMaterial: "suntalam", doorColor: "d140" },
                  },
                  {
                    label: "Gardırop",
                    w: 240, h: 220, d: 60, tid: "preset-gardirop",
                    extra: { doorStyle: "swing", doorless: false, doorLayout: "full", doors: 4, dividers: 3, shelves: 3, drawers: 0, base: "none", doorMaterial: "suntalam", doorColor: "d140" },
                  },
                  {
                    label: "Kitaplık",
                    w: 120, h: 200, d: 35, tid: "preset-kitaplik",
                    extra: { doorStyle: "swing", doorless: true, doorLayout: "full", doors: 2, dividers: 1, shelves: 5, drawers: 0, base: "none", doorMaterial: "suntalam", doorColor: "d140" },
                  },
                  {
                    label: "Vestiyer",
                    w: 140, h: 220, d: 40, tid: "preset-vestiyer",
                    extra: { doorStyle: "swing", doorless: false, doorLayout: "full", doors: 3, dividers: 2, shelves: 1, drawers: 0, base: "none", doorMaterial: "suntalam", doorColor: "d140" },
                  },
                  {
                    label: "Banyo Üst (Aynalı)",
                    w: 80, h: 70, d: 20, tid: "preset-banyo-ust",
                    extra: { doorStyle: "swing", doorless: false, doorLayout: "full", doors: 2, dividers: 1, shelves: 2, drawers: 0, base: "none", doorMaterial: "ayna", doorColor: "d140" },
                  },
                ].map((p) => {
                  const activePreset = cfg.width === p.w && cfg.height === p.h && cfg.depth === p.d;
                  return (
                    <button
                      key={p.tid}
                      type="button"
                      data-testid={p.tid}
                      title={`${p.label}: ölçüler + önerilen ayarlar uygulanır`}
                      onClick={() => {
                        setCfg((prev) => ({ ...prev, width: p.w, height: p.h, depth: p.d, ...p.extra }));
                        toast.success(`${p.label} ölçüleri ve önerilen ayarlar uygulandı.`);
                      }}
                      className={`rounded-full border px-2.5 py-1 text-[11px] font-medium transition-colors ${
                        activePreset
                          ? "border-primary bg-primary/10 text-primary"
                          : "border-[#E6DFD5] text-stone-600 hover:border-primary hover:text-primary"
                      }`}
                    >
                      {p.label}
                      <span className="font-mono text-stone-400 ml-1">
                        {p.w}×{p.h}×{p.d}
                      </span>
                    </button>
                  );
                })}
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <Label>Raf Sayısı</Label>
                  <span className="font-mono text-sm text-primary font-semibold">
                    {effectiveShelves > cfg.shelves
                      ? `${cfg.shelves} + ${effectiveShelves - cfg.shelves} bölme rafı`
                      : `${cfg.shelves} raf`}
                  </span>
                </div>
                <Slider
                  data-testid="designer-shelves-slider"
                  value={[cfg.shelves]}
                  min={0}
                  max={8}
                  step={1}
                  onValueChange={(v) => set("shelves", v[0])}
                />
                {splitActive && (
                  <p data-testid="designer-shelf-align-note" className="text-[11px] text-stone-500 mt-1.5">
                    Parçalı kapakta raflar kapak bitimlerine otomatik hizalanır — kapak açıldığında bölme rafı tam kapak sınırında görünür.
                  </p>
                )}
              </div>

              <div>
                <Label className="mb-2 block">Kapak</Label>
                <div className="grid grid-cols-3 gap-2" data-testid="designer-doormode-select">
                  {[
                    { id: "swing", label: "Kapaklı" },
                    { id: "flap", label: "Düşer Kapak" },
                    { id: "none", label: "Kapaksız" },
                  ].map((m) => {
                    const active =
                      m.id === "none" ? cfg.doorless : !cfg.doorless && cfg.doorStyle === m.id;
                    const disabled = m.id === "flap" && (cfg.height > 60 || cfg.height < 20);
                    return (
                      <button
                        key={m.id}
                        type="button"
                        disabled={disabled}
                        data-testid={`designer-doormode-${m.id === "swing" ? "kapakli" : m.id === "flap" ? "duser" : "kapaksiz"}`}
                        onClick={() =>
                          setCfg((p) => ({
                            ...p,
                            doorless: m.id === "none",
                            doorStyle: m.id === "none" ? p.doorStyle : m.id,
                          }))
                        }
                        className={`rounded-lg border px-2 py-2 text-xs font-medium transition-all disabled:opacity-40 disabled:cursor-not-allowed ${
                          active
                            ? "border-primary bg-primary/10 text-primary shadow-sm"
                            : "border-[#E6DFD5] text-stone-600 hover:border-stone-300"
                        }`}
                      >
                        {m.label}
                      </button>
                    );
                  })}
                </div>
                {flapActive && (
                  <p data-testid="designer-flap-note" className="text-[11px] text-stone-500 mt-1.5">
                    Düşer kapak aşağı doğru açılır — yalnızca 20-60 cm yükseklikte kullanılabilir (TV üniteleri için). Daha yüksek ünitelerde normal kapak kullanılır.
                  </p>
                )}
                {flapActive && (
                  <div className="mt-3" data-testid="designer-flap-handle-toggle">
                    <Label className="mb-2 block text-xs">Kulp Tipi</Label>
                    <div className="grid grid-cols-2 gap-2">
                      {[
                        { id: "handle", label: "Kulplu" },
                        { id: "push", label: "Bas-aç (Kulpsuz)" },
                      ].map((o) => (
                        <button
                          key={o.id}
                          type="button"
                          data-testid={`designer-flap-handle-${o.id}`}
                          onClick={() => set("flapHandle", o.id)}
                          className={`rounded-lg border px-2 py-2 text-xs font-medium transition-all ${
                            cfg.flapHandle === o.id
                              ? "border-primary bg-primary/10 text-primary shadow-sm"
                              : "border-[#E6DFD5] text-stone-600 hover:border-stone-300"
                          }`}
                        >
                          {o.label}
                        </button>
                      ))}
                    </div>
                    {cfg.flapHandle === "push" && (
                      <p data-testid="designer-flap-push-note" className="text-[11px] text-stone-500 mt-1.5">
                        Bas-aç seçildi — kulplar kaldırıldı ve kulp bedeli fiyattan düşüldü.
                      </p>
                    )}
                  </div>
                )}
                {!flapActive && cfg.height > 60 && (
                  <p data-testid="designer-flap-range-note" className="text-[11px] text-stone-400 mt-1.5">
                    Düşer kapak yalnızca 20-60 cm yükseklikte seçilebilir.
                  </p>
                )}
                {cfg.doorless && (
                  <div className="mt-3" data-testid="designer-backpanel-toggle">
                    <Label className="mb-2 block text-xs">Arka Panel (Arkalık)</Label>
                    <div className="grid grid-cols-2 gap-2">
                      {[
                        { id: false, label: "Arkalıklı" },
                        { id: true, label: "Arkalıksız" },
                      ].map((o) => (
                        <button
                          key={String(o.id)}
                          type="button"
                          data-testid={o.id ? "designer-backpanel-off" : "designer-backpanel-on"}
                          onClick={() => set("noBackPanel", o.id)}
                          className={`rounded-lg border px-2 py-2 text-xs font-medium transition-all ${
                            cfg.noBackPanel === o.id
                              ? "border-primary bg-primary/10 text-primary shadow-sm"
                              : "border-[#E6DFD5] text-stone-600 hover:border-stone-300"
                          }`}
                        >
                          {o.label}
                        </button>
                      ))}
                    </div>
                    {cfg.noBackPanel && (
                      <p data-testid="designer-backpanel-note" className="text-[11px] text-stone-500 mt-1.5">
                        Arkalık iptal edildi — arka panel maliyeti fiyattan düşülür. Yalnızca kapaksız tasarımlarda geçerlidir.
                      </p>
                    )}
                  </div>
                )}
              </div>

              {!cfg.doorless && (
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <Label>Kapak Sayısı</Label>
                    <span className="font-mono text-sm text-primary font-semibold">{cfg.doors} kapak</span>
                  </div>
                  <Slider
                    data-testid="designer-doors-slider"
                    value={[cfg.doors]}
                    min={minDoors}
                    max={maxDoors}
                    step={1}
                    onValueChange={(v) => set("doors", v[0])}
                  />
                  {minDoors > 1 && (
                    <p data-testid="designer-door-min-note" className="text-[11px] text-stone-500 mt-1.5">
                      100 cm üzeri kapak kullanılmaz — bu genişlikte minimum {minDoors} kapak gerekir.
                    </p>
                  )}
                </div>
              )}

              {!cfg.doorless && cfg.doorStyle === "swing" && (
                <div>
                  <Label className="mb-2 block">Kapak Tipi</Label>
                  <div className="grid grid-cols-3 gap-2" data-testid="designer-doorlayout-select">
                    {[
                      { id: "full", label: "Boydan" },
                      { id: "split", label: "Üst + Alt" },
                      { id: "split3", label: "Üst + Orta + Alt" },
                    ].map((m) => {
                      const disabled =
                        (m.id === "split" && cfg.height < 100) ||
                        (m.id === "split3" && cfg.height < 140);
                      return (
                        <button
                          key={m.id}
                          type="button"
                          disabled={disabled}
                          data-testid={`designer-doorlayout-${m.id}`}
                          onClick={() => set("doorLayout", m.id)}
                          className={`rounded-lg border px-2 py-2 text-xs font-medium transition-all disabled:opacity-40 disabled:cursor-not-allowed ${
                            cfg.doorLayout === m.id
                              ? "border-primary bg-primary/10 text-primary shadow-sm"
                              : "border-[#E6DFD5] text-stone-600 hover:border-stone-300"
                          }`}
                        >
                          {m.label}
                        </button>
                      );
                    })}
                  </div>
                  {cfg.doorLayout !== "full" && (
                    <p data-testid="designer-split-note" className="text-[11px] text-stone-500 mt-1.5">
                      Kapak parçaları bağımsız açılır — her parçaya tıklayabilirsiniz.
                    </p>
                  )}
                  {cfg.height < 100 ? (
                    <p data-testid="designer-split-min-note" className="text-[11px] text-stone-400 mt-1.5">
                      Parçalı kapak için minimum 100 cm yükseklik gerekir.
                    </p>
                  ) : cfg.height < 140 ? (
                    <p data-testid="designer-split3-min-note" className="text-[11px] text-stone-400 mt-1.5">
                      Üst + Orta + Alt kapak için minimum 140 cm yükseklik gerekir.
                    </p>
                  ) : null}
                </div>
              )}

              <div>
                <div className="flex items-center justify-between mb-2">
                  <Label>Orta Dikme Sayısı</Label>
                  <span className="font-mono text-sm text-primary font-semibold">{cfg.dividers} dikme</span>
                </div>
                <Slider
                  data-testid="designer-dividers-slider"
                  value={[cfg.dividers]}
                  min={0}
                  max={maxDividers}
                  step={1}
                  onValueChange={(v) => set("dividers", v[0])}
                />
              </div>

              {!mirrorDrawerBlocked && (
                <div data-testid="designer-drawers-section">
                  <div className="flex items-center justify-between mb-2">
                    <Label>Çekmece Adedi</Label>
                    <span className="font-mono text-sm text-primary font-semibold">
                      {flapDrawerBlocked ? 0 : cfg.drawers} sıra × {cfg.dividers + 1} bölme = {(flapDrawerBlocked ? 0 : cfg.drawers) * (cfg.dividers + 1)} çekmece
                    </span>
                  </div>
                  <Slider
                    data-testid="designer-drawers-slider"
                    value={[flapDrawerBlocked ? 0 : cfg.drawers]}
                    min={0}
                    max={maxDrawers}
                    step={1}
                    disabled={flapDrawerBlocked}
                    className={flapDrawerBlocked ? "opacity-40 pointer-events-none" : ""}
                    onValueChange={(v) => !flapDrawerBlocked && set("drawers", v[0])}
                  />
                  {flapDrawerBlocked && (
                    <p data-testid="designer-drawer-flap-note" className="text-[11px] text-stone-500 mt-1.5">
                      Düşer kapaklı TV ünitesinde çekmece kullanılmaz — çekmece eklemek için kapakları kaldırın veya Kapaksız seçin.
                    </p>
                  )}
                </div>
              )}

              {cfg.drawers > 0 && (
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <Label>Çekmece Yüksekliği</Label>
                    <span className="font-mono text-sm text-primary font-semibold">
                      {cfg.drawerHeight} cm
                    </span>
                  </div>
                  <Slider
                    data-testid="designer-drawer-height-slider"
                    value={[cfg.drawerHeight]}
                    min={10}
                    max={35}
                    step={1}
                    onValueChange={(v) => set("drawerHeight", v[0])}
                  />
                </div>
              )}

              <div>
                <Label className="mb-2 block">Alt Bölüm</Label>
                <div className="grid grid-cols-3 gap-2" data-testid="designer-base-select">
                  {[
                    { id: "none", label: "Yok" },
                    { id: "alt_kapama", label: "Alt Kapama (8 cm)" },
                    { id: "ayak", label: "Ayak (10 cm)" },
                  ].map((b) => (
                    <button
                      key={b.id}
                      type="button"
                      data-testid={`designer-base-${b.id}`}
                      onClick={() => set("base", b.id)}
                      className={`rounded-lg border px-2 py-2 text-xs font-medium transition-all ${
                        cfg.base === b.id
                          ? "border-primary bg-primary/10 text-primary shadow-sm"
                          : "border-[#E6DFD5] text-stone-600 hover:border-stone-300"
                      }`}
                    >
                      {b.label}
                    </button>
                  ))}
                </div>
                {cfg.base === "ayak" && (
                  <div
                    data-testid="designer-leg-warning"
                    className="mt-2 rounded-lg bg-amber-50 border border-amber-300 text-amber-800 text-xs p-2.5 flex items-center gap-2"
                  >
                    <AlertTriangle className="w-4 h-4 shrink-0" />
                    Ayak seçildiğinde dolap boyu 10 cm daha uzayacaktır.
                  </div>
                )}
              </div>

              <MaterialPicker
                label="Gövde Malzemesi"
                value={cfg.bodyMaterial}
                onChange={(v) => set("bodyMaterial", v)}
                options={BODY_MATERIALS}
                priceMap={prices.body}
                testid="designer-body-material-select"
              />
              <ColorPicker
                label="Gövde Rengi"
                value={cfg.bodyColor}
                onChange={(v) => set("bodyColor", v)}
                options={BODY_COLORS}
                testid="designer-body-color-select"
              />
              <MaterialPicker
                label="Kapak Modeli"
                value={cfg.doorMaterial}
                onChange={(v) =>
                  setCfg((p) => ({
                    ...p,
                    doorMaterial: v,
                    doorColor:
                      v === "cam" ? "cam_seffaf" : p.doorColor.startsWith("cam_") ? "d143" : p.doorColor,
                  }))
                }
                options={DOOR_MATERIALS}
                priceMap={prices.door}
                testid="designer-door-material-select"
              />
              {cfg.doorMaterial === "ayna" ? (
                <div data-testid="designer-mirror-note" className="rounded-lg border border-[#E6DFD5] bg-[#F5F0EB] px-3 py-2.5">
                  <Label className="flex items-center gap-1.5">
                    <PaintBucket className="w-3.5 h-3.5 text-primary" /> Kapak Rengi
                  </Label>
                  <p className="text-[11px] text-stone-500 mt-1">
                    Ayna kapakta renk seçilmez — ayna tam kapak yüzeyini kaplar ve kapaklar kulpsuzdur.
                  </p>
                </div>
              ) : (
                <ColorPicker
                  label={cfg.doorMaterial === "cam" ? "Cam Rengi" : "Kapak Rengi"}
                  value={cfg.doorColor}
                  onChange={(v) => set("doorColor", v)}
                  options={cfg.doorMaterial === "cam" ? GLASS_COLORS : DOOR_COLORS}
                  testid="designer-door-color-select"
                  tag={cfg.doorMaterial === "cam" ? null : "Kastamonu Entegre"}
                />
              )}
            </div>

            <CostPanel cost={cost} />
          </div>
        </div>
      </div>

      <CheckoutDialog
        open={checkoutOpen}
        onOpenChange={setCheckoutOpen}
        config={full}
        cost={cost}
        cnc={cnc}
      />
    </div>
  );
}
